import { seededRandom } from './board';

/** Keep large lexicons out of generation workers; prefer unseen targets. */
export function variedCandidates(words: string[], recent: Iterable<string>, maximumLength: number, count: number, seed: string): string[] {
  const exposure = [...recent], used = new Set(exposure), seen = new Set<string>();
  const age = new Map(exposure.map((word, index) => [word, index]));
  const fresh: string[] = [], repeated: string[] = [];
  for (const word of words) {
    if (seen.has(word) || [...word].length < 3 || [...word].length > maximumLength) continue;
    seen.add(word); (used.has(word) ? repeated : fresh).push(word);
  }
  const random = seededRandom(seed);
  for (const list of [fresh]) for (let i = list.length - 1; i > 0; i--) {
    const index = Math.floor(random() * (i + 1));
    [list[i], list[index]] = [list[index]!, list[i]!];
  }
  // History is oldest first: exhausted pools reintroduce older words first.
  repeated.sort((a, b) => age.get(a)! - age.get(b)!);
  return fresh.length >= count ? fresh.slice(0, 128) : [...fresh, ...repeated.slice(0, Math.max(0, count - fresh.length))];
}

export type ExposureHistory = Partial<Record<'en' | 'es', string[]>>;
export const exposureLimit = 2000;
/** Distinct, oldest-first targets; repeated exposure moves a word to the end. */
export function rememberTargets(history: ExposureHistory, locale: string, words: string[]): ExposureHistory {
  if (locale !== 'en' && locale !== 'es') return history;
  const ordered = new Set(history[locale] ?? []);
  for (const word of words) {
    if (typeof word !== 'string' || [...word].length < 3 || [...word].length > 32) continue;
    ordered.delete(word); ordered.add(word);
  }
  return { ...history, [locale]: [...ordered].slice(-exposureLimit) };
}

export function recoverExposure(...sources: unknown[]): ExposureHistory {
  let history: ExposureHistory = {};
  for (const source of sources) {
    if (!source || typeof source !== 'object' || Array.isArray(source)) continue;
    for (const locale of ['en', 'es'] as const) {
      const words = (source as ExposureHistory)[locale];
      if (!Array.isArray(words)) continue;
      history = rememberTargets(history, locale, words.slice(-exposureLimit).filter(word => typeof word === 'string'
        && (locale === 'en' ? /^[A-Z]{3,32}$/ : /^[A-ZÁÉÍÓÚÜÑ]{3,32}$/u).test(word)));
    }
  }
  return history;
}
